import { CommonModule } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { finalize } from "rxjs";
import { LearningInsight, ProfileData } from "../../models/game.models";
import { AuthService } from "../../services/auth.service";
import { GameService } from "../../services/game.service";

@Component({
  selector: "app-profile",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: "./profile.component.html",
  styleUrl: "./profile.component.css"
})
export class ProfileComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  profile: ProfileData | null = null;
  insights: LearningInsight | null = null;
  loading = true;
  error = "";
  savingProfile = false;
  savingPassword = false;
  avatarUploading = false;
  profileMessage = "";
  profileError = "";
  avatarMessage = "";
  avatarError = "";
  passwordMessage = "";
  passwordError = "";
  avatarPreviewUrl = "";

  readonly profileForm = this.fb.nonNullable.group({
    name: ["", [Validators.required, Validators.minLength(2), Validators.maxLength(64)]],
    phone: [""]
  });

  readonly passwordForm = this.fb.nonNullable.group({
    currentPassword: ["", [Validators.required, Validators.minLength(6)]],
    newPassword: [
      "",
      [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^A-Za-z0-9]).{8,}$/)
      ]
    ],
    confirmPassword: ["", [Validators.required, Validators.minLength(8)]]
  });

  constructor(
    private readonly gameService: GameService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadProfile();
  }

  saveProfile() {
    if (!this.profileForm || this.profileForm.invalid || this.savingProfile) {
      return;
    }

    const value = this.profileForm.getRawValue();
    this.savingProfile = true;
    this.profileMessage = "";
    this.profileError = "";

    this.gameService
      .updateProfile({
        name: value.name.trim(),
        phone: value.phone?.trim() || null
      })
      .pipe(finalize(() => (this.savingProfile = false)))
      .subscribe({
        next: (updated) => {
          if (this.profile) {
            this.profile = {
              ...this.profile,
              name: updated.name,
              phone: updated.phone ?? null,
              avatar: updated.avatar ?? null
            };
            this.avatarPreviewUrl = this.profile.avatar || "";
          }
          this.profileMessage = "Profile updated successfully.";
        },
        error: (error) => {
          this.profileError = error?.error?.message || "Failed to update profile";
        }
      });
  }

  updatePassword() {
    if (!this.passwordForm || this.passwordForm.invalid || this.savingPassword) {
      return;
    }

    const value = this.passwordForm.getRawValue();
    if (value.newPassword !== value.confirmPassword) {
      this.passwordError = "Passwords do not match.";
      return;
    }

    const email = this.profile?.email;
    if (!email) {
      this.passwordError = "Email is required to change password.";
      return;
    }

    this.savingPassword = true;
    this.passwordMessage = "";
    this.passwordError = "";

    this.authService
      .changePassword(email, value.currentPassword, value.newPassword)
      .pipe(finalize(() => (this.savingPassword = false)))
      .subscribe({
        next: (result) => {
          this.passwordMessage = result.message || "Password updated successfully.";
          this.passwordForm.reset();
        },
        error: (error) => {
          this.passwordError = error?.error?.message || error?.message || "Failed to update password";
        }
      });
  }

  onAvatarSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    this.avatarMessage = "";
    this.avatarError = "";
    if (!file) {
      return;
    }
    if (!file.type.startsWith("image/")) {
      this.avatarError = "Please choose an image file.";
      return;
    }
    const maxBytes = 2 * 1024 * 1024;
    if (file.size > maxBytes) {
      this.avatarError = "Image must be 2MB or smaller.";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      if (!dataUrl) {
        this.avatarError = "Failed to read the image.";
        return;
      }
      this.avatarPreviewUrl = dataUrl;
      this.uploadAvatar(dataUrl);
    };
    reader.onerror = () => {
      this.avatarError = "Failed to read the image.";
    };
    reader.readAsDataURL(file);
  }

  removeAvatar() {
    if (this.avatarUploading) {
      return;
    }
    this.avatarMessage = "";
    this.avatarError = "";
    this.avatarUploading = true;
    this.gameService
      .updateProfile({ avatar: null })
      .pipe(finalize(() => (this.avatarUploading = false)))
      .subscribe({
        next: (updated) => {
          if (this.profile) {
            this.profile = {
              ...this.profile,
              avatar: updated.avatar ?? null
            };
          }
          this.avatarPreviewUrl = "";
          this.avatarMessage = "Avatar removed.";
        },
        error: (error) => {
          this.avatarError = error?.error?.message || "Failed to remove avatar.";
        }
      });
  }

  private loadProfile() {
    this.loading = true;
    this.error = "";

    this.gameService.getProfile().subscribe({
      next: (profile) => {
        this.profile = profile;
        if (this.profileForm) {
          this.profileForm.patchValue({
            name: profile.name || "",
            phone: profile.phone || ""
          });
        }
        this.avatarPreviewUrl = profile.avatar || "";
        this.authService.syncStats({
          xp: profile.xp,
          level: profile.level,
          streak: profile.streak
        });
        this.loading = false;
      },
      error: (error) => {
        this.error = error?.error?.message || "Failed to load profile";
        this.loading = false;
      }
    });

    this.gameService.getInsights().subscribe({
      next: (insights) => {
        this.insights = insights;
      }
    });
  }

  private uploadAvatar(dataUrl: string) {
    this.avatarUploading = true;
    this.avatarMessage = "";
    this.avatarError = "";
    this.gameService
      .uploadAvatar(dataUrl)
      .pipe(finalize(() => (this.avatarUploading = false)))
      .subscribe({
        next: (result) => {
          if (this.profile) {
            this.profile = {
              ...this.profile,
              avatar: result.avatar
            };
          }
          this.avatarPreviewUrl = result.avatar;
          this.avatarMessage = "Avatar updated.";
        },
        error: (error) => {
          this.avatarError = error?.error?.message || "Failed to upload avatar.";
        }
      });
  }

  getInitials(name?: string | null) {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      return "JS";
    }
    const parts = trimmed.split(/\s+/).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase()).join("") || "JS";
  }
}
